import config from "@/config/config";
import { getUserInitials } from "@/lib/user-initials";

/**
 * Gets the URL for a user's profile photo
 * @param photoFilename - The filename of the profile photo (from Student.PhotoFilename)
 * @returns The full URL to the profile photo, or empty string if no photo
 */
export const getProfilePhotoUrl = (photoFilename?: string | null): string => {
  const trimmed = photoFilename?.trim();
  if (!trimmed) {
    return "";
  }

  const basename = trimmed.split(/[/\\]/).pop() ?? "";
  if (!basename || basename.includes("..") || trimmed.includes("..")) {
    return "";
  }

  return `${config.apiBaseUrl.replace("/api", "")}/profile-photos/${encodeURIComponent(basename)}`;
};

/**
 * Gets the default avatar URL (fallback when user has no profile photo)
 * @param userId - Optional user ID to generate a consistent default avatar
 * @returns URL to a default avatar (using dicebear API or local asset)
 */
export const getDefaultAvatarUrl = (userId?: string): string => {
  const seed = encodeURIComponent(userId || "default");
  return `https://api.dicebear.com/9.x/notionists/svg?seed=${seed}&size=200`;
};

/**
 * Gets the avatar URL for a user (profile photo or default)
 * @param photoFilename - The filename of the profile photo
 * @param userId - Optional user ID for generating default avatar
 * @returns The URL to use for the avatar (profile photo or default)
 */
export const getAvatarUrl = (
  photoFilename?: string | null,
  userId?: string,
): string => {
  const profilePhotoUrl = getProfilePhotoUrl(photoFilename);
  if (profilePhotoUrl) {
    return profilePhotoUrl;
  }
  return getDefaultAvatarUrl(userId);
};

/**
 * Resolves avatar src from API avatar object, profile filename, or user id.
 */
export const resolveAvatarSrc = (options: {
  avatarUrl?: string | null;
  photoFilename?: string | null;
  userId?: string;
}): string => {
  const explicitUrl = options.avatarUrl?.trim();
  if (explicitUrl) {
    return explicitUrl;
  }
  return getAvatarUrl(options.photoFilename, options.userId);
};

/** @deprecated Prefer `getUserInitials` from `@/lib/user-initials`. */
export const getAvatarInitials = getUserInitials;
